import { useMemo } from 'react';
import { FeatureGate } from '../../components/FeatureGate';
import { useFeature } from '../../lib/features';
import { useNavigate } from 'react-router-dom';
import { AlertCircle, Sparkles, Wand2 } from 'lucide-react';
import { ScreenHeader } from '../../components/ui/ScreenHeader';
import { AppCard } from '../../components/ui/AppCard';
import {
  connectorCatalog,
  connectorCategoryLabel,
  type ConnectorCategory,
  type ConnectorDefinition,
} from '../../lib/connectors';

// Browse-connectors screen (roadmap #9 Phase 1, D4 - see src/lib/connectors.ts
// for the truthfulness rationale). Grouped by customer intent per
// docs/ZMIST_INTEGRATION_TOOL_DESIGN.md §5.2, not by internal protocol.
// Clicking a connector goes to the setup route for its id - the actual
// guided setup flow (GitHub fast path, generic-webhook visual editor) is a
// later slice, so today that route only shows a placeholder pointing back
// at the existing Advanced Mapping form. No fake "coming soon" connector is
// shown here - every entry in the registry genuinely works today.
const categoryOrder: ConnectorCategory[] = ['engineering', 'metrics', 'business', 'custom'];

function ConnectorCard({ connector }: { connector: ConnectorDefinition }) {
  const navigate = useNavigate();
  return (
    <AppCard
      role="button"
      tabIndex={0}
      onClick={() => navigate(`/integrations/new/${connector.id}`)}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') navigate(`/integrations/new/${connector.id}`);
      }}
      className="flex flex-col gap-1.5 cursor-pointer hover:bg-white/90 dark:hover:bg-white/[0.09] transition-colors"
    >
      <span className="font-semibold">{connector.name}</span>
      <span className="text-sm text-black/60 dark:text-white/60">{connector.description}</span>
      {connector.setupKind === 'agent_required' && (
        <span className="mt-1 inline-flex items-start gap-1.5 rounded-lg bg-amber-500/10 text-amber-700 dark:text-amber-400 text-xs font-medium px-2.5 py-1.5">
          <AlertCircle size={14} className="shrink-0 mt-0.5" aria-hidden />
          {connector.agentSetupHint}
        </span>
      )}
    </AppCard>
  );
}

export function ConnectorCatalogScreen() {
  const navigate = useNavigate();
  const salesforceOn = useFeature('integrations.salesforce');
  const byCategory = useMemo(() => {
    const groups = new Map<ConnectorCategory, ConnectorDefinition[]>();
    for (const c of connectorCatalog) {
      if (c.id === 'salesforce' && !salesforceOn) continue;
      const list = groups.get(c.category) ?? [];
      list.push(c);
      groups.set(c.category, list);
    }
    return groups;
  }, [salesforceOn]);

  return (
    <div className="flex flex-col gap-4">
      <ScreenHeader title="Browse connectors" backTo="/integrations" />
      <FeatureGate feature="integrations.ai_mapping" fallback={null}>
      <AppCard
        role="button"
        tabIndex={0}
        onClick={() => navigate('/integrations/new/ai')}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') navigate('/integrations/new/ai');
        }}
        className="flex flex-col gap-1.5 cursor-pointer border-blue-500/30 hover:bg-white/90 dark:hover:bg-white/[0.09] transition-colors"
      >
        <span className="font-semibold inline-flex items-center gap-1.5">
          <Sparkles size={16} aria-hidden /> AI integration
        </span>
        <span className="text-sm text-black/60 dark:text-white/60">
          Drop a file or paste your data and press one button: the AI writes a strictly validated mapping, proves it on your example, and sets up the webhook to receive more.
        </span>
      </AppCard>
      <AppCard
        role="button"
        tabIndex={0}
        onClick={() => navigate('/integrations/new/auto')}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') navigate('/integrations/new/auto');
        }}
        className="flex flex-col gap-1.5 cursor-pointer border-blue-500/30 hover:bg-white/90 dark:hover:bg-white/[0.09] transition-colors"
      >
        <span className="font-semibold inline-flex items-center gap-1.5">
          <Wand2 size={16} aria-hidden /> Auto-map from an example
        </span>
        <span className="text-sm text-black/60 dark:text-white/60">
          Paste a few records (or just field names) in JSON, CSV, TSV, XML, YAML, a log stream or Prometheus metrics, pick how it arrives and where it goes - the
          mapping is generated and previewed for you.
        </span>
      </AppCard>
      </FeatureGate>
      {categoryOrder.map((category) => {
        const connectors = byCategory.get(category);
        if (!connectors || connectors.length === 0) return null;
        return (
          <div key={category} className="flex flex-col gap-2">
            <h2 className="text-xs font-semibold uppercase tracking-wide text-black/40 dark:text-white/40 m-0">
              {connectorCategoryLabel[category]}
            </h2>
            <div className="flex flex-col gap-3">
              {connectors.map((c) => (
                <ConnectorCard key={c.id} connector={c} />
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
}
